export interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

export interface ViewportAnchoredPositionInput {
  readonly anchorRect: DOMRectReadOnly;
  readonly floatingWidth: number;
  readonly floatingHeight: number;
  readonly margin?: number;
  readonly viewport?: ViewportSize;
}

export interface FloatingDimensions {
  readonly width: number;
  readonly height: number;
}

export const DEFAULT_COLOR_PICKER_ESTIMATED_SIZE: FloatingDimensions = {
  width: 260,
  height: 360,
};

/**
 * Measures a floating portal wrapper, falling back to its first child or scroll
 * dimensions when absolutely-positioned content collapses the wrapper to 0×0.
 */
export function measureFloatingDimensions(
  element: HTMLElement | undefined,
  fallback?: FloatingDimensions
): FloatingDimensions {
  if (!element) {
    return fallback ?? { width: 0, height: 0 };
  }

  let width = element.offsetWidth;
  let height = element.offsetHeight;

  if (width === 0 || height === 0) {
    const child = element.firstElementChild;
    if (child instanceof HTMLElement) {
      const rect = child.getBoundingClientRect();
      width = Math.max(width, Math.round(rect.width));
      height = Math.max(height, Math.round(rect.height));
    }
  }

  if (width === 0 || height === 0) {
    width = Math.max(width, element.scrollWidth);
    height = Math.max(height, element.scrollHeight);
  }

  if (fallback !== undefined && (width === 0 || height === 0)) {
    return {
      width: width || fallback.width,
      height: height || fallback.height,
    };
  }

  return { width, height };
}

export interface ViewportAnchoredPosition {
  readonly left: number;
  readonly top: number;
}

const DEFAULT_MARGIN_PX = 8;

/**
 * Computes fixed viewport coordinates for a floating element anchored to a trigger,
 * flipping above the anchor and clamping to the viewport when needed.
 */
export function computeViewportAnchoredPosition(
  input: ViewportAnchoredPositionInput
): ViewportAnchoredPosition {
  const margin = input.margin ?? DEFAULT_MARGIN_PX;
  const viewport = input.viewport ?? {
    width: window.innerWidth,
    height: window.innerHeight,
  };
  const { anchorRect, floatingWidth: pickerW, floatingHeight: pickerH } = input;

  let left = anchorRect.left + anchorRect.width + margin;
  let top = anchorRect.bottom + margin;

  if (pickerH > 0 && top + pickerH > viewport.height - margin) {
    const topAbove = anchorRect.top - pickerH - margin;
    if (topAbove >= margin) {
      top = topAbove;
    } else {
      top = Math.max(margin, viewport.height - pickerH - margin);
    }
  }

  if (pickerW > 0) {
    if (left + pickerW > viewport.width - margin) {
      left = anchorRect.left - pickerW - margin;
    }
    left = Math.max(margin, Math.min(left, viewport.width - pickerW - margin));
  }

  return { left, top };
}
